import { AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, inject, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { ChimeService } from '../chime.service';

@Component({
  selector: 'app-chat-box',
  standalone: true,
  imports: [FormsModule, TranslateModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule],
  styleUrls: ['./chat-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chat-box-container">
      <div class="chat-header">
        <h3>{{ 'VideoCall.Chat.Title' | translate }}</h3>
        <button mat-icon-button (click)="closeChatWindow()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div #chatContainer class="chat-body" (change)="onNewMessage()">
        @for (msg of messages(); track msg.timestamp) {
          <div class="message-wrapper" [class.outgoing]="msg.senderId === localUserId()" [class.incoming]="msg.senderId !== localUserId()">
            <div class="avatar">
              <mat-icon>{{ msg.senderId === localUserId() ? 'person' : 'chat' }}</mat-icon>
            </div>
            <div class="message-content">
              <div class="sender-name">{{ msg.senderName }}</div>
              <div class="message-text">{{ msg.message }}</div>
            </div>
          </div>
        }
      </div>

      @if (isChatLocked() && !isHost()) {
        <div class="chat-disabled-message p-3 text-center text-slate-500 text-sm font-medium border-t border-slate-100 bg-slate-50/50">
          {{ 'VideoCall.Chat.Disabled' | translate }}
        </div>
      } @else {
        <div class="chat-input-bar">
          <input
            matInput
            class="chat-input"
            [placeholder]="'VideoCall.ChatBox.Placeholder' | translate"
            [ngModel]="currentMessage()"
            (ngModelChange)="currentMessage.set($event)"
            (keyup.enter)="onSendMessage()"
          />
          <button mat-icon-button class="send-button" (click)="onSendMessage()" [disabled]="!currentMessage().trim()">
            <mat-icon>send</mat-icon>
          </button>
        </div>
      }
    </div>
  `,
})
export class ChatBoxComponent implements AfterViewChecked {
  private chimeService = inject(ChimeService);

  messages = this.chimeService.chatMessages;
  localUserId = this.chimeService.localUserId;
  isChatLocked = this.chimeService.isChatLocked;
  isHost = this.chimeService.isHost;

  closeChat = output<void>();

  chatContainer = viewChild.required<ElementRef>('chatContainer');

  currentMessage = signal('');
  private needsScroll = true;

  ngAfterViewChecked(): void {
    if (this.needsScroll) {
      this.scrollToBottom();
      this.needsScroll = false;
    }
  }

  onNewMessage(): void {
    this.needsScroll = true;
  }

  onSendMessage(): void {
    const message = this.currentMessage().trim();
    if (message) {
      this.chimeService.sendMessage(message);
      this.currentMessage.set('');
    }
  }

  closeChatWindow() {
    this.closeChat.emit();
    this.chimeService.closeChatPane();
  }

  private scrollToBottom(): void {
    try {
      this.chatContainer().nativeElement.scrollTop = this.chatContainer().nativeElement.scrollHeight;
    } catch (err) {
      console.error('Could not scroll to bottom:', err);
    }
  }
}
